import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Award, Loader2, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Results() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ student_name: "", student_email: "", subject: "", exam_type: "End-Sem", marks_obtained: "", total_marks: 100, semester: 5 });

  useEffect(() => { fetchResults(); }, []);

  const fetchResults = async () => {
    try {
      const { data: d } = await axios.get(`${API}/results`, { withCredentials: true });
      setData(d);
    } catch {} finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/results`, { ...form, marks_obtained: parseFloat(form.marks_obtained), total_marks: parseFloat(form.total_marks), semester: parseInt(form.semester) }, { withCredentials: true });
      setShowModal(false); fetchResults();
    } catch {}
  };

  const GRADE_COLORS = { "A+": "#22C55E", "A": "#0A7AFF", "B+": "#8B5CF6", "B": "#F59E0B", "C": "#F97316", "F": "#FF3636" };

  return (
    <div className="space-y-6" data-testid="results-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Academic</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Results & Evaluation</h2>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="add-result-btn"
          className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
          <Award className="w-3.5 h-3.5" /> Add Result
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Results", value: data.length, color: "#0A7AFF" },
          { label: "Grade A+", value: data.filter(d => d.grade === "A+").length, color: "#22C55E" },
          { label: "Pass %", value: data.length > 0 ? `${Math.round(data.filter(d => d.grade !== "F").length / data.length * 100)}%` : "0%", color: "#8B5CF6" },
          { label: "Avg Score", value: data.length > 0 ? `${(data.reduce((s, d) => s + d.percentage, 0) / data.length).toFixed(1)}%` : "—", color: "#F59E0B" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="bg-white border border-[#E2DDD4]">
        <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "1.5fr 1.5fr 1fr 80px 80px 80px 60px" }}>
          {["Student", "Subject", "Type", "Marks", "Score", "Sem", "Grade"].map(h => (
            <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
          ))}
        </div>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
          : data.map((item, i) => (
          <div key={item._id} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "1.5fr 1.5fr 1fr 80px 80px 80px 60px" }}>
            <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{item.student_name}</p>
            <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{item.subject}</span>
            <span className="font-['DM_Sans'] text-xs text-[#BDBAA8]">{item.exam_type}</span>
            <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10]">{item.marks_obtained}/{item.total_marks}</span>
            <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">{item.percentage}%</span>
            <span className="font-['DM_Sans'] text-xs text-[#7A7468]">Sem {item.semester}</span>
            <div className="w-8 h-8 flex items-center justify-center font-['Syne'] font-bold text-sm" style={{ background: (GRADE_COLORS[item.grade] || "#BDBAA8") + "15", color: GRADE_COLORS[item.grade] || "#BDBAA8" }}>
              {item.grade}
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6"><h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Add Result</h3><button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button></div>
              <form onSubmit={handleAdd} className="space-y-4">
                {[["student_name", "Student Name"], ["student_email", "Email"], ["subject", "Subject"], ["marks_obtained", "Marks Obtained"], ["total_marks", "Total Marks"], ["semester", "Semester"]].map(([f, l]) => (
                  <div key={f}><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">{l}</label>
                  <input type={["marks_obtained", "total_marks", "semester"].includes(f) ? "number" : "text"} value={form[f]} onChange={e => setForm(p => ({...p, [f]: e.target.value}))} required
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" /></div>
                ))}
                <div><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">Exam Type</label>
                <select value={form.exam_type} onChange={e => setForm(p => ({...p, exam_type: e.target.value}))}
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none">
                  <option>Mid-Sem</option><option>End-Sem</option><option>Lab</option>
                </select></div>
                <button type="submit" className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors mt-2">Add Result</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
